import { useLocation } from "wouter";
import { CountrySelector } from "@/components/auth/CountrySelector.tsx";
import { SmsConsentCheckbox } from "@/components/auth/SmsConsentCheckbox.tsx";
import { Button } from "@/components/ui/button.tsx";
import { TypingText } from "@/components/ui/TypingText.tsx";
import { useOnboardingAnimation } from "@/hooks/useOnboardingAnimation.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { apiErrorMessage, loggableRequestError } from "@/shared/apiErrorMessage.ts";
import { countryList } from "@/shared/countryList.ts";
import { applyPhoneEdit, defaultCountryName, toE164 } from "@/shared/phoneInput.ts";
import { isAxiosError } from "axios";
import { useState } from "react";
import { PhoneAlreadyTakenPanel } from "../PhoneAlreadyTakenPanel.tsx";
import { useSubmitPhoneNumber } from "../hooks/usePhoneVerification.tsx";

export const SubmitPhoneStep = ({
  onCodeSent,
  onDone,
}: {
  /** A code is on its way; go collect it. */
  onCodeSent: () => void;
  /** Nothing left to ask and no setup thread came back. */
  onDone: () => void;
}) => {
  const { userContext } = useCurrentUserContext();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [selectedCountryName, setSelectedCountryName] = useState(() =>
    defaultCountryName(countryList),
  );
  const [, setLocation] = useLocation();
  const [error, setError] = useState("");
  const [phoneAlreadyTaken, setPhoneAlreadyTaken] = useState(false);

  const selectedCountry = countryList.find((c) => c.name === selectedCountryName) ?? countryList[0];
  const e164PhoneNumber = toE164(phoneNumber, selectedCountry);
  const { mutateAsync: submitPhoneNumber, isPending } = useSubmitPhoneNumber();

  // Use the abstracted animation hook
  const {
    animationState,
    handleFirstTextComplete,
    handleSecondTextComplete,
    handleThirdTextComplete,
  } = useOnboardingAnimation({ isActive: true });

  const handleSubmit = async () => {
    if (e164PhoneNumber === null) return;
    setError("");

    try {
      // Server saves the number and conditionally sends OTP based on smsConsent
      const resp = await submitPhoneNumber({ phoneNumber: e164PhoneNumber, smsConsent });

      // An already-verified number ends the phone flow here rather than at
      // verify, so this response is the one that has to do what verify normally
      // does: hand the user to the setup thread.
      if (resp.onboardingThreadId !== undefined) {
        setLocation(`/threads/${resp.onboardingThreadId}`, { replace: true });
        return;
      }

      // Already verified means no OTP was sent, so sending the user to the
      // verify screen would strand them on a code that never arrives (BIG-5146).
      if (resp.alreadyVerified) {
        onDone();
        return;
      }

      onCodeSent();
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.code === "PHONE_ALREADY_TAKEN") {
        setPhoneAlreadyTaken(true);
        setError(err.response.data.error);
      } else if (isAxiosError(err) && err.response?.data?.code === "TOO_MANY_SENDS") {
        // The server names the wait; "try again" would only spend another send.
        setError(apiErrorMessage(err) ?? "Too many codes sent. Please wait and try again.");
      } else {
        console.error("Failed to submit phone number:", loggableRequestError(err));
        setError("Failed to send code. Please try again.");
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(applyPhoneEdit(e.target.value, phoneNumber, selectedCountry));
  };

  const handleCountryChange = (countryName: string) => {
    setSelectedCountryName(countryName);
    // Grouping is per numbering plan, so a partially typed number formatted for
    // the old country is meaningless under the new one.
    setPhoneNumber("");
  };

  const canContinue = e164PhoneNumber !== null && smsConsent;

  return (
    <div className="flex flex-col">
      <div className="space-y-6 flex-shrink-0 md:pb-6">
        <div className="flex flex-col gap-2 text-4xl font-serif text-zinc-900 leading-tight">
          {animationState.showFirstText && (
            <TypingText
              text={`Welcome to Okthx, ${userContext?.user.userContact.displayShortName}!`}
              onComplete={handleFirstTextComplete}
            />
          )}
          {animationState.showSecondText && (
            <TypingText
              text="To get started, please share your phone number."
              onComplete={handleSecondTextComplete}
            />
          )}
        </div>
        <div className="text-base text-zinc-900 leading-6">
          {animationState.showThirdText && (
            <TypingText
              text="This enables Okthx to quickly reach you or send an urgent reminder."
              onComplete={handleThirdTextComplete}
            />
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col justify-center pt-6 px-1">
        <div className="md:w-1/2">
          {phoneAlreadyTaken ? (
            <PhoneAlreadyTakenPanel error={error} />
          ) : (
            <>
              <div
                className={`flex bg-white border border-zinc-200 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-foreground focus-within:ring-offset-2 transition-all duration-700 ${
                  animationState.complete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <div className="flex items-center py-3 border-r border-zinc-200">
                  <CountrySelector
                    value={selectedCountryName}
                    onValueChange={handleCountryChange}
                  />
                </div>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && canContinue) {
                      e.preventDefault();
                      handleSubmit();
                    }
                  }}
                  placeholder="Phone number"
                  className="flex flex-1 px-4 py-3 text-base text-zinc-900 placeholder-zinc-400 bg-transparent border-none outline-none min-w-0"
                />
              </div>
              <div
                className={`m-2 transition-all duration-800 ${
                  animationState.complete ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
              >
                <SmsConsentCheckbox checked={smsConsent} onCheckedChange={setSmsConsent} />
              </div>
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              {animationState.complete && (
                <div className="mt-4">
                  <Button
                    onClick={handleSubmit}
                    disabled={!canContinue || isPending}
                    data-statsig-name="onboarding-phone-send-code"
                  >
                    {isPending ? "Sending..." : "Send code"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
